import { h } from 'preact'
import { batch } from '@preact/signals'
import { t } from '../chat-i18n.js'

/**
 * InConversationSearch — search bar embedded in RoomHeader.
 *
 * Filters store.currentMessages.value bodies and updates:
 *   store.searchQuery, store.searchMatches, store.searchIdx
 *
 * Props: { store, onClose }
 */
export function InConversationSearch({ store, onClose }) {
  // Auto-focus on mount
  const focusInput = (el) => {
    if (el) requestAnimationFrame(() => el.focus())
  }

  function handleInput(e) {
    const q = e.target.value
    store.searchQuery.value = q

    if (!q.trim()) {
      batch(() => {
        store.searchMatches.value = []
        store.searchIdx.value     = 0
      })
      return
    }

    const lower   = q.toLowerCase()
    const matches = store.currentMessages.value
      .filter(m => (m.body || m.body_html || '').toLowerCase().includes(lower))
      .map(m => m.id)

    batch(() => {
      store.searchMatches.value = matches
      store.searchIdx.value     = matches.length > 0 ? matches.length - 1 : 0
    })

    // Scroll to current match
    scrollToMatch(matches[matches.length - 1] ?? null)
  }

  function prev() {
    const matches = store.searchMatches.value
    if (!matches.length) return
    const newIdx = store.searchIdx.value <= 0
      ? matches.length - 1
      : store.searchIdx.value - 1
    store.searchIdx.value = newIdx
    scrollToMatch(matches[newIdx])
  }

  function next() {
    const matches = store.searchMatches.value
    if (!matches.length) return
    const newIdx = store.searchIdx.value >= matches.length - 1
      ? 0
      : store.searchIdx.value + 1
    store.searchIdx.value = newIdx
    scrollToMatch(matches[newIdx])
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter')  { e.shiftKey ? prev() : next() }
    if (e.key === 'Escape') { handleClose() }
  }

  function handleClose() {
    batch(() => {
      store.searchQuery.value   = ''
      store.searchMatches.value = []
      store.searchIdx.value     = 0
    })
    onClose?.()
  }

  function scrollToMatch(msgId) {
    if (!msgId) return
    const el = document.getElementById(`chat-message-${msgId}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const matches  = store.searchMatches.value
  const idx      = store.searchIdx.value
  const hasQuery = store.searchQuery.value.trim().length > 0
  const counter  = matches.length > 0
    ? `${idx + 1} / ${matches.length}`
    : hasQuery ? '0 / 0' : ''

  return (
    <div class="chat-panel-search-bar is-open">
      <div class="chat-panel-search-inner">
        <i class="bi bi-search chat-panel-search-icon" />
        <input
          ref={el => focusInput(el)}
          type="search"
          class="chat-panel-search-input"
          placeholder={t('chat.messages.search_placeholder')}
          value={store.searchQuery.value}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
        />
        {counter && (
          <span class="chat-panel-search-counter">{counter}</span>
        )}
        <button
          type="button"
          class="chat-panel-action-btn"
          title={t('chat.messages.search_toggle')}
          disabled={!matches.length}
          onClick={prev}
        >
          <i class="bi bi-chevron-up" />
        </button>
        <button
          type="button"
          class="chat-panel-action-btn"
          title={t('chat.messages.search_toggle')}
          disabled={!matches.length}
          onClick={next}
        >
          <i class="bi bi-chevron-down" />
        </button>
        <button
          type="button"
          class="chat-panel-action-btn"
          title={t('chat.dock.close')}
          onClick={handleClose}
        >
          <i class="bi bi-x-lg" />
        </button>
      </div>
    </div>
  )
}
